import { call, put, takeLatest } from 'redux-saga/effects';
import { startOfDay } from 'date-fns';
import * as api from 'api/billingCoupon';
import { change, reset, SubmissionError } from 'redux-form';
import { SOME_BUSINESS_ID_INVALID } from 'constants/billingCoupon/validationConstants';
import {
  COUPON_FORM,
  COUPON_QUANTITY,
  MERCHANT_QUANTITY,
} from 'constants/billingCoupon/formConstants';
import showLoadingSpinner from 'state/loadingSpinner/actions';
import * as toastActions from 'state/toast/actions';
import {
  CHANGE_COUPON_DETAILS_LOGS_PAGE,
  CHANGE_COUPON_STATUS,
  COUPONS_LIST_REQUEST,
  RESET_EDITOR_MODAL,
  SHOW_COUPON_DETAILS_ROUTINE,
} from 'state/actionTypes';
import * as couponActions from 'state/coupons/actions';

export async function getCouponsList(paging, filters) {
  const {
    data: {
      data: { coupons },
      paging: resultPaging,
    },
  } = await api.getBillingCoupons(paging, {
    ...filters,
    searchKeyword: (filters.searchKeyword || '').toUpperCase(),
  });
  return {
    list: coupons,
    paging: resultPaging,
  };
}

export function* fetchCouponsList({ payload }) {
  yield put(showLoadingSpinner(true));
  try {
    const couponsResult = yield call(
      getCouponsList,
      payload.paging,
      payload.filters
    );
    yield put(
      couponActions.updateCouponsList(couponsResult.list, couponsResult.paging)
    );
  } catch (error) {
    if (error.response && error.response.data && error.response.data.errors) {
      yield put(
        toastActions.errorMessage(
          'Fetch coupons failed',
          error.response.data.errors[0]
        )
      );
    } else {
      yield put(
        toastActions.errorMessage('Fetch coupons failed', error.message)
      );
    }
  } finally {
    yield put(showLoadingSpinner(false));
  }
}

function makeCreateCouponPayload(coupon) {
  const data = {
    code: coupon.code,
    status: 'OPEN',
    issuer_id: coupon.issuerId,
    started_at: new Date(),
    ended_at: startOfDay(coupon.expiration),
  };

  if (coupon.subscriptionLength) {
    data.subscription_length = coupon.subscriptionLength;
  }

  if (coupon.discountReward) {
    data.discount_percentage = parseFloat(coupon.amount.replace(',', '.'));
    data.max_discount_per_application = coupon.maxDiscount;
  }

  if (coupon.subscriptionExtension) {
    data.additional_month = coupon.additionalMonth;
  }

  if (coupon.quotaType === COUPON_QUANTITY) {
    data.includes_all_businesses = true;
    data.global_quota = coupon.quantity;
  } else if (coupon.quotaType === MERCHANT_QUANTITY) {
    data.includes_all_businesses = false;
    data.business_ids = coupon.businessIds;
  }
  return data;
}

export function* addNewCoupon({ payload }) {
  const { coupon, paging, filters } = payload;

  try {
    yield put(couponActions.setCouponEditorLoading(true));

    const data = makeCreateCouponPayload(coupon);

    yield call(api.addNewCoupon, data);
    yield put(couponActions.showEditorModal(false));
    yield put(toastActions.successMessage('Coupons added successfully'));
    yield put(couponActions.getCouponsList(paging, filters));
    yield put(couponActions.createCouponForm.success());
  } catch (ex) {
    yield put(couponActions.setEditorModalFormDirty(false));
    const errors = {
      _error: ex.message.errors,
    };
    if (ex.message.business_id_validation) {
      yield put(
        couponActions.setCouponBusinessIdValidationResult(
          ex.message.business_id_validation
        )
      );

      if (ex.message.business_id_validation.invalid_count > 0) {
        errors.businessIds = SOME_BUSINESS_ID_INVALID;
      }
    }
    yield put(
      couponActions.createCouponForm.failure(new SubmissionError(errors))
    );
  } finally {
    yield put(couponActions.setCouponEditorLoading(false));
  }
}

export function postChangeCouponStatus(couponCode, action) {
  return api.updateBillingCoupon(couponCode, action);
}

export function* changeCouponStatus({ payload }) {
  const { couponCode, action, paging, filters } = payload;
  yield put(couponActions.setCouponToggleConfirmLoading(true));
  try {
    yield call(postChangeCouponStatus, couponCode, action);
    yield put(
      toastActions.successMessage(
        `Successfully changed coupon status for ${couponCode}`
      )
    );
  } catch (error) {
    yield put(
      toastActions.errorMessage(
        `Change coupon status for ${couponCode} failed`,
        error.message
      )
    );
  } finally {
    yield put(couponActions.setCouponToggleConfirmLoading(false));
  }
  yield put(couponActions.showCouponToggleConfirmation(false));
  yield put(couponActions.getCouponsList(paging, filters));
}

export function* showCouponDetails({
  payload: { couponCode, logsPageNumber, logsPageSize },
}) {
  yield put(couponActions.setCouponDetailsValues(null));
  yield put(couponActions.setCouponDetailsLogs([]));
  yield put(couponActions.showCouponDetailsErrorMessage(false));
  yield put(couponActions.showCouponDetailsModal(true));
  yield put(couponActions.setCouponDetailsLoading(true));
  try {
    const { data: couponDetails } = yield call(
      api.getBillingCouponDetails,
      couponCode
    );
    yield put(couponActions.setCouponDetailsValues(couponDetails.data));

    const { data: couponLogs } = yield call(
      api.getBillingCouponLogs,
      couponCode,
      logsPageNumber,
      logsPageSize
    );
    yield put(
      couponActions.setCouponDetailsLogs(
        couponLogs.data.internal_tools_activities,
        couponLogs.paging.page,
        couponLogs.paging.size,
        couponLogs.paging.total
      )
    );
  } catch (e) {
    yield put(couponActions.showCouponDetailsErrorMessage(true, e.message));
  } finally {
    yield put(couponActions.setCouponDetailsLoading(false));
  }
}

export function* setCouponDetailsLogs({
  payload: { couponCode, pageNumber, pageSize },
}) {
  yield put(couponActions.setCouponDetailsLogsLoading(true));
  yield put(couponActions.showCouponDetailsErrorMessage(false));
  try {
    const { data: couponLogs } = yield call(
      api.getBillingCouponLogs,
      couponCode,
      pageNumber,
      pageSize
    );
    yield put(
      couponActions.setCouponDetailsLogs(
        couponLogs.data.internal_tools_activities,
        couponLogs.paging.page,
        couponLogs.paging.size,
        couponLogs.paging.total
      )
    );
  } catch (e) {
    yield put(couponActions.showCouponDetailsErrorMessage(true, e.message));
  } finally {
    yield put(couponActions.setCouponDetailsLogsLoading(false));
  }
}

export function* resetEditorModal() {
  yield put(reset(COUPON_FORM));
  yield put(change(COUPON_FORM, 'quotaType', COUPON_QUANTITY));
  yield put(couponActions.setEditorModalFormDirty(true));
  yield put(couponActions.setCouponBusinessIdValidationResult(null));
  yield put(couponActions.setCouponEditorErrorMessage(null));
}

export default function* saga() {
  yield takeLatest(COUPONS_LIST_REQUEST, fetchCouponsList);
  yield takeLatest(couponActions.createCouponForm.REQUEST, addNewCoupon);
  yield takeLatest(CHANGE_COUPON_STATUS, changeCouponStatus);
  yield takeLatest(SHOW_COUPON_DETAILS_ROUTINE, showCouponDetails);
  yield takeLatest(RESET_EDITOR_MODAL, resetEditorModal);
  yield takeLatest(CHANGE_COUPON_DETAILS_LOGS_PAGE, setCouponDetailsLogs);
}
